/**
 * Segment rule catalogue and validation (SEG-10 to SEG-12).
 * A rule tree is groups (`op` + `children`) and conditions (`field`, `operator`, `value`),
 * nested at most 2 deep.
 */
import type { RuleCondition, RuleGroup, RuleNode, RuleOperator } from '../../components/customers/data/types';

/**
 * Loyalty tiers are not in the live Loyalty module yet. Turning this on offers the
 * `loyalty_tier` rule field and the `{{loyalty_tier}}` message token.
 */
export const TIERS_ENABLED = false;

export const MAX_RULE_DEPTH = 2;

export type ValueKind = 'int' | 'money' | 'bool' | 'month' | 'list';
export type OptionSource =
  | 'items' | 'categories' | 'outlets' | 'orderChannels' | 'dayparts' | 'tiers' | 'campaigns'
  | 'tags' | 'groups' | 'riskStatuses' | 'lifecycleStates';
export type FieldGroup = 'Orders' | 'Loyalty' | 'Profile' | 'Campaigns';

export interface FieldDef {
  field: string;
  label: string;
  group: FieldGroup;
  operators: RuleOperator[];
  valueKind: ValueKind;
  supportsPeriod?: boolean;
  optionSource?: OptionSource;
  unit?: string;
  /** Used by prebuilt segments only; the custom builder does not offer it. */
  prebuiltOnly?: boolean;
}

/** SEG-11 — supported fields and operators. */
export const FIELD_CATALOG: FieldDef[] = [
  { field: 'last_order_days_ago', label: 'Days since last order', group: 'Orders', operators: ['lt', 'gt', 'between'], valueKind: 'int', unit: 'days' },
  { field: 'first_order_days_ago', label: 'Days since first order', group: 'Orders', operators: ['lt', 'gt', 'between'], valueKind: 'int', unit: 'days' },
  { field: 'order_count', label: 'Number of orders', group: 'Orders', operators: ['eq', 'gte', 'lte', 'between'], valueKind: 'int', supportsPeriod: true, unit: 'orders' },
  { field: 'total_spend_cents', label: 'Total spend', group: 'Orders', operators: ['gte', 'lte', 'between'], valueKind: 'money', supportsPeriod: true },
  { field: 'avg_order_value_cents', label: 'Average order value', group: 'Orders', operators: ['gte', 'lte'], valueKind: 'money' },
  { field: 'ordered_item_id', label: 'Ordered item', group: 'Orders', operators: ['in', 'not_in'], valueKind: 'list', optionSource: 'items', supportsPeriod: true },
  { field: 'ordered_category_id', label: 'Ordered from category', group: 'Orders', operators: ['in', 'not_in'], valueKind: 'list', optionSource: 'categories', supportsPeriod: true },
  { field: 'order_channel', label: 'Order channel', group: 'Orders', operators: ['in'], valueKind: 'list', optionSource: 'orderChannels' },
  { field: 'outlet_id', label: 'Ordered at outlet', group: 'Orders', operators: ['in'], valueKind: 'list', optionSource: 'outlets' },
  { field: 'daypart', label: 'Time of day', group: 'Orders', operators: ['in'], valueKind: 'list', optionSource: 'dayparts' },
  { field: 'loyalty_member', label: 'Loyalty member', group: 'Loyalty', operators: ['eq'], valueKind: 'bool' },
  { field: 'loyalty_tier', label: 'Loyalty tier', group: 'Loyalty', operators: ['in'], valueKind: 'list', optionSource: 'tiers' },
  { field: 'points_balance', label: 'Points balance', group: 'Loyalty', operators: ['gte', 'lte'], valueKind: 'int', unit: 'points' },
  { field: 'has_unredeemed_reward', label: 'Has an unredeemed reward', group: 'Loyalty', operators: ['eq'], valueKind: 'bool' },
  { field: 'points_expiring_within_days', label: 'Points expiring within', group: 'Loyalty', operators: ['lte'], valueKind: 'int', unit: 'days' },
  { field: 'birthday_month', label: 'Birthday month', group: 'Profile', operators: ['eq'], valueKind: 'month' },
  { field: 'has_app', label: 'Uses the Customer App', group: 'Profile', operators: ['eq'], valueKind: 'bool' },
  { field: 'tag', label: 'Tag', group: 'Profile', operators: ['in', 'not_in'], valueKind: 'list', optionSource: 'tags' },
  { field: 'group_id', label: 'Customer group', group: 'Profile', operators: ['in', 'not_in'], valueKind: 'list', optionSource: 'groups' },
  { field: 'risk_status', label: 'Risk flag', group: 'Profile', operators: ['in', 'not_in'], valueKind: 'list', optionSource: 'riskStatuses' },
  { field: 'lifecycle_state', label: 'Customer status', group: 'Profile', operators: ['in', 'not_in'], valueKind: 'list', optionSource: 'lifecycleStates' },
  { field: 'received_campaign_id', label: 'Received campaign', group: 'Campaigns', operators: ['in', 'not_in'], valueKind: 'list', optionSource: 'campaigns' },
  { field: 'redeemed_campaign_id', label: 'Redeemed campaign', group: 'Campaigns', operators: ['in', 'not_in'], valueKind: 'list', optionSource: 'campaigns' },
  // Prebuilt-only (SEG-05, SEG-08, SEG-09)
  { field: 'spend_top_percent', label: 'Top spenders', group: 'Orders', operators: ['lte'], valueKind: 'int', supportsPeriod: true, unit: '%', prebuiltOnly: true },
  { field: 'birthday_this_month', label: 'Birthday this month', group: 'Profile', operators: ['eq'], valueKind: 'bool', prebuiltOnly: true },
  { field: 'reachable', label: 'Reachable on any channel', group: 'Profile', operators: ['eq'], valueKind: 'bool', prebuiltOnly: true },
];

export const OPERATOR_LABELS: Record<RuleOperator, string> = {
  lt: 'less than',
  gt: 'more than',
  between: 'between',
  eq: 'is',
  gte: 'at least',
  lte: 'at most',
  in: 'is any of',
  not_in: 'is none of',
};

export function getFieldDef(field: string): FieldDef | undefined {
  return FIELD_CATALOG.find((f) => f.field === field);
}

export const FIELD_GROUPS: FieldGroup[] = ['Orders', 'Loyalty', 'Profile', 'Campaigns'];

/** Fields the custom rule builder offers. */
export function builderFields(): FieldDef[] {
  return FIELD_CATALOG.filter((f) => !f.prebuiltOnly && (TIERS_ENABLED || f.field !== 'loyalty_tier'));
}

export function isGroup(node: RuleNode): node is RuleGroup {
  return (node as RuleGroup).children !== undefined;
}

export function defaultValueFor(def: FieldDef, operator: RuleOperator): RuleCondition['value'] {
  if (def.valueKind === 'bool') return true;
  if (def.valueKind === 'list') return [];
  if (def.valueKind === 'month') return 1;
  return operator === 'between' ? [0, 0] : 0;
}

export function newCondition(field = 'last_order_days_ago'): RuleCondition {
  const def = getFieldDef(field) ?? FIELD_CATALOG[0];
  const operator = def.operators[0];
  return { field: def.field, operator, value: defaultValueFor(def, operator) };
}

export interface RuleError {
  /** Index path from the root group to the failing node. */
  path: number[];
  message: string;
}

const isInt = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v);

function valueProblem(def: FieldDef, cond: RuleCondition): string | null {
  const { operator, value } = cond;
  switch (def.valueKind) {
    case 'bool':
      return typeof value === 'boolean' ? null : 'needs a yes or no value';
    case 'month':
      return isInt(value) && value >= 1 && value <= 12 ? null : 'needs a month from 1 to 12';
    case 'list':
      if (!Array.isArray(value) || value.some((v) => typeof v !== 'string')) return 'needs a list of choices';
      return value.length > 0 ? null : 'needs at least one choice';
    default:
      if (operator === 'between') {
        if (!Array.isArray(value) || value.length !== 2 || !isInt(value[0]) || !isInt(value[1])) return 'needs a from and a to number';
        if (value[0] < 0 || value[1] < 0) return 'cannot be negative';
        return value[0] <= value[1] ? null : 'the from value must not be greater than the to value';
      }
      if (!isInt(value)) return 'needs a whole number';
      return value >= 0 ? null : 'cannot be negative';
  }
}

function describe(path: number[]): string {
  return `Condition ${path.map((i) => i + 1).join('.')}`;
}

function validateNode(node: RuleNode, path: number[], depth: number, errors: RuleError[]) {
  if (isGroup(node)) {
    if (depth > MAX_RULE_DEPTH) {
      errors.push({ path, message: `Groups can be nested at most ${MAX_RULE_DEPTH} levels deep` });
      return;
    }
    if (node.op !== 'and' && node.op !== 'or') {
      errors.push({ path, message: 'A group must combine its conditions with "and" or "or"' });
    }
    if (!Array.isArray(node.children) || node.children.length === 0) {
      errors.push({ path, message: path.length === 0 ? 'Add at least one condition' : 'A group needs at least one condition' });
      return;
    }
    node.children.forEach((child, i) => validateNode(child, [...path, i], depth + 1, errors));
    return;
  }
  const def = getFieldDef(node.field);
  if (!def) {
    errors.push({ path, message: `${describe(path)}: unknown field "${node.field}"` });
    return;
  }
  if (!def.operators.includes(node.operator as RuleOperator)) {
    errors.push({ path, message: `${describe(path)}: "${def.label}" does not support the operator "${node.operator}"` });
    return;
  }
  const problem = valueProblem(def, node);
  if (problem) errors.push({ path, message: `${describe(path)}: "${def.label}" ${problem}` });
  if (node.periodDays !== undefined) {
    if (!def.supportsPeriod) errors.push({ path, message: `${describe(path)}: "${def.label}" does not take a time period` });
    else if (!isInt(node.periodDays) || node.periodDays <= 0) errors.push({ path, message: `${describe(path)}: the time period must be a positive number of days` });
  }
}

/** SEG-12 — every bad condition is reported, each naming the condition. */
export function validateRules(rules: RuleGroup): RuleError[] {
  const errors: RuleError[] = [];
  validateNode(rules, [], 1, errors);
  return errors;
}

export function countConditions(node: RuleNode): number {
  return isGroup(node) ? node.children.reduce((n, c) => n + countConditions(c), 0) : 1;
}
